import { axios, transformAxiosError } from "../utils/axios.create";
import { BackOfficeUser } from "../domain/user.interface";
import { Program } from "../domain/programs.interface";
import { IRoleAction } from "@/domain/role.interface";

export type UsersPayload = {
  sortField: string;
  sortOrder: number;
  pageNumber: number;
  pageSize: number;
  search: string;
  status?: string[];
  roleId?: string[];
  programId?: string;
  orgUnitId?: string[];
};

export enum GetUserIncludes {
  roleIds = "roleIds",
}

export interface GetUsersForProgram {
  programId: string;
  orgUnitId: string;
}

const findAll = async ({
  sortField,
  sortOrder,
  pageNumber,
  pageSize,
  search,
  status,
  roleId,
  programId,
  orgUnitId,
}: UsersPayload): Promise<{
  users: BackOfficeUser[];
  totalCount: number;
}> => {
  const queryParams = new URLSearchParams({
    pageNumber: String(pageNumber + 1),
    pageSize: String(pageSize),
    sortField,
    sortOrder: sortOrder === 1 ? "asc" : "desc",
    search,
    status: (status || []).join(","),
    roleId: (roleId || []).join(","),
    programId: programId || "",
    orgUnitId: (orgUnitId || []).join(","),
  });

  return axios
    .get(`/users/search?${queryParams.toString()}`)
    .then((res) => {
      const body = res.data.body;
      return {
        users: body.users,
        totalCount: body.totalCount,
      };
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const findByEmail = async (email: string): Promise<BackOfficeUser> => {
  const queryParams = new URLSearchParams({
    email,
    includes: [GetUserIncludes.roleIds].join(","),
  });
  return axios
    .get(`/users/email?${queryParams.toString()}`)
    .then((res) => {
      return (res.data.body as BackOfficeUser) || [];
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const getCmCoordinator = async ({
  programId,
  orgUnitId,
}: GetUsersForProgram): Promise<BackOfficeUser[]> => {
  const queryParams = new URLSearchParams({
    programId,
    orgUnitId,
  });

  return axios
    .get(`/users/cm-coordinator?${queryParams.toString()}`)
    .then((res) => {
      const body = res.data.body;
      return body;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const getAdditionalStaff = async ({
  programId,
  orgUnitId,
}: GetUsersForProgram): Promise<BackOfficeUser[]> => {
  const queryParams = new URLSearchParams({
    programId,
    orgUnitId,
  });

  return axios
    .get(`/users/additional-staff?${queryParams.toString()}`)
    .then((res) => {
      const body = res.data.body;
      return body;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const findRolesById = async (id: string): Promise<IRoleAction[]> => {
  return axios
    .get(`users/${id}/roles`)
    .then((res) => {
      return (res.data.body as IRoleAction[]) || [];
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const findProgramsByUser = async ({
  id,
  sortType,
}: {
  id: string;
  sortType: string;
}): Promise<Program[]> => {
  return axios
    .get(`users/${id}/programs`)
    .then((res) => {
      if (sortType === "desc") {
        return (res.data.body as Program[]).reverse() || [];
      }

      return (res.data.body as Program[]) || [];
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const createOne = async (user: BackOfficeUser): Promise<BackOfficeUser> => {
  return axios
    .post("/users", user)
    .then((res) => {
      return res.data.body as BackOfficeUser;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const updateOne = async ({
  user,
  userId,
}: {
  user: BackOfficeUser;
  userId: string;
}): Promise<BackOfficeUser> => {
  return axios
    .put(`/users/${userId}`, user)
    .then((res) => {
      return res.data.body as BackOfficeUser;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const deleteOne = async ({ userId }: { userId: string }): Promise<void> => {
  return axios
    .delete(`/users/${userId}`)
    .then(() => {
      return;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const resendInvitation = async (email: string): Promise<void> => {
  const queryParams = new URLSearchParams({
    email,
  }).toString();

  return axios
    .post(`/users/resend-invitation?${queryParams}`)
    .then(() => {
      return;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const findById = async (id: string): Promise<BackOfficeUser> => {
  return axios
    .get(`/users/find/${id}`)
    .then((res) => {
      return res.data.body as BackOfficeUser;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

export const UsersService = {
  findAll,
  findById,
  findByEmail,
  getCmCoordinator,
  getAdditionalStaff,
  findRolesById,
  findProgramsByUser,
  createOne,
  updateOne,
  deleteOne,
  resendInvitation,
};
